/**
 * ServiceWizardRunner — generic, data-driven assistant to create a Service.
 *
 * One component drives EVERY wizard: it reads a declarative definition
 * (steps + questions) from /api/service-wizards, asks the questions, shows a
 * preview (nothing is persisted until confirmed) and then applies it.
 *
 * Nothing here is technology-specific; add a new assistant by editing data.
 */
import React, { useState, useEffect, useCallback } from 'react';
import { XMarkIcon, ArrowLeftIcon, ArrowRightIcon, CheckIcon, MagnifyingGlassIcon, PlusIcon, PencilSquareIcon, TrashIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import './ServiceDesigner.css';

const CI_TYPE_LABELS = {
  service: 'Servicio', rack_element: 'Servidor/Storage', virt_vm: 'VM',
  firewall: 'Firewall', router: 'Router', workstation: 'Puesto',
  wifi_controller: 'Controladora WiFi', wifi_access_point: 'AP',
  ipam_subnet: 'Subred', ipam_ip: 'IP', printer: 'Impresora',
  server_volume: 'Volumen', storage_lun: 'LUN', storage_disk: 'Disco',
  isp_circuit: 'Circuito ISP', virt_hypervisor: 'Hypervisor',
  virt_cluster: 'Clúster', virt_datastore: 'Datastore', rack: 'Rack',
  wifi_ssid: 'SSID WiFi', logical_component: 'Grupo lógico',
  database_instance: 'Base de datos',
};

// Etiquetas de element_type de rack_element (para explicar los filtros).
const ELEMENT_TYPE_LABELS = {
  server: 'servidor físico', storage: 'cabina de almacenamiento', switch: 'switch',
  pdu: 'PDU', panel: 'panel', tray: 'bandeja', router: 'router',
  firewall: 'firewall', ont: 'ONT',
};

const CRITICALITY = [
  { value: 'critical', label: 'Crítico' }, { value: 'high', label: 'Alto' },
  { value: 'medium', label: 'Medio' }, { value: 'low', label: 'Bajo' },
];

// Tipos de alojamiento de `Service.hosting_type` (los mismos labels que la ficha).
const HOSTING_LABELS = {
  vm: 'máquina virtual', bare_metal: 'servidor físico', docker: 'contenedores (Docker)',
  saas: 'servicio externo', paas: 'PaaS', cloud_vm: 'VM en la nube',
};

function normOptions(opts) {
  return (opts || []).map((o) => (typeof o === 'string' ? { value: o, label: o } : o));
}

function showQuestion(q, answers) {
  const cond = q.show_if;
  if (!cond) return true;
  // Se admite una condición o una lista (todas deben cumplirse).
  if (Array.isArray(cond)) return cond.every((c) => showQuestion({ ...q, show_if: c }, answers));
  const v = answers[cond.q];
  const isEmpty = v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
  if ('eq' in cond) return v === cond.eq;
  if ('ne' in cond) return v !== cond.ne;
  if ('in' in cond) return (cond.in || []).includes(v);
  if ('empty' in cond) return cond.empty ? isEmpty : !isEmpty;
  return true;
}

// ── CI browser — lists the company's CIs from the start (no typing needed) ────
function CiBrowser({ companyId, ciTypes, onPick, placeholder, isPicked, filter }) {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const reqId = React.useRef(0);
  const typesKey = (ciTypes || []).join(',');
  const filterKey = filter ? JSON.stringify(filter) : '';
  const isMultiType = (ciTypes || []).length > 1;

  const load = useCallback(async (text, type) => {
    const id = ++reqId.current;
    setBusy(true);
    try {
      const types = type ? [type] : (typesKey ? typesKey.split(',') : []);
      const res = await api.sdSearchCIs(companyId, types, (text || '').trim(), 60,
        filterKey ? JSON.parse(filterKey) : undefined);
      if (id === reqId.current) { setResults(res.data || []); setErr(''); }
    } catch (e) {
      // OJO: callarse aquí es lo que ocultó un 500 del backend (el selector
      // decía «no hay elementos» y parecía que no había datos).
      if (id === reqId.current) { setResults([]); setErr(e?.message || 'No se pudo buscar'); }
    } finally {
      if (id === reqId.current) setBusy(false);
    }
  }, [companyId, typesKey, filterKey]);

  useEffect(() => { load('', ''); }, [load]);

  const onFilter = (text) => { setQ(text); load(text, typeFilter); };
  const onType = (t) => { const nt = t === typeFilter ? '' : t; setTypeFilter(nt); load(q, nt); };

  const groups = {};
  results.forEach((r) => { (groups[r.ci_type] = groups[r.ci_type] || []).push(r); });
  const typesPresent = Object.keys(groups);

  const filterNote = (() => {
    if (!filter) return '';
    const parts = [];
    Object.entries(filter).forEach(([ciType, cols]) => {
      // La nota sólo tiene sentido para los tipos que se están listando: al
      // elegir una VM no debe decir «Solo: servidor físico».
      if (typesKey && !typesKey.split(',').includes(ciType)) return;
      Object.values(cols || {}).forEach((v) => {
        const vals = Array.isArray(v) ? v : [v];
        parts.push(vals.map((x) => ELEMENT_TYPE_LABELS[x] || x).join(' o '));
      });
    });
    return parts.join(' · ');
  })();

  return (
    <div className="sw-browse">
      <div className="sw-search-box">
        <MagnifyingGlassIcon width={15} height={15} />
        <input className="sd-builder-input" value={q} placeholder={placeholder || 'Filtrar por nombre…'}
          onChange={(e) => onFilter(e.target.value)} />
        {!!q && <button type="button" className="sw-x" onClick={() => onFilter('')}><XMarkIcon width={13} height={13} /></button>}
      </div>
      {isMultiType && (
        <div className="sw-choices sw-browse-chips">
          {(ciTypes || []).map((t) => (
            <button type="button" key={t} className={typeFilter === t ? 'is-on' : ''} onClick={() => onType(t)}>
              {CI_TYPE_LABELS[t] || t}
            </button>
          ))}
        </div>
      )}
      <div className="sw-browse-meta">
        {err
          ? <span className="sw-browse-err">⚠ No se pudieron cargar los elementos: {err}</span>
          : (busy ? 'Cargando…' : `${results.length} elemento${results.length === 1 ? '' : 's'}`)}
        {!err && results.length >= 60 && ' · refina con el filtro'}
        {!err && filterNote && <span className="sw-browse-note">Solo: {filterNote}</span>}
      </div>
      <div className="sw-browse-list">
        {typesPresent.map((t) => (
          <div key={t}>
            {isMultiType && <div className="sw-browse-group">{CI_TYPE_LABELS[t] || t}</div>}
            <ul className="sw-search-results">
              {groups[t].map((r) => {
                const picked = isPicked ? isPicked(r) : false;
                return (
                  <li key={`${r.ci_type}:${r.ci_id}`}>
                    <button type="button" className={picked ? 'is-picked' : ''} onClick={() => onPick(r)}>
                      {!isMultiType && <span className="sw-chip">{CI_TYPE_LABELS[r.ci_type] || r.ci_type}</span>}
                      <span className="sw-browse-info">
                        <span className="sw-browse-name">{r.name}</span>
                        {/* El backend manda a veces el mismo texto como detalle
                            (p. ej. un servidor cuyo `fqdn` es su nombre): no se
                            repite, que ensuciaba la lista. */}
                        {r.description && String(r.description).trim() !== String(r.name || '').trim() && (
                          <span className="sw-browse-desc">{r.description}</span>
                        )}
                      </span>
                      {r.delegated && (
                        <span className="sw-chip sw-chip-deleg" title={`Cedido a esta empresa por ${r.owner_company_name || 'otra empresa'}`}>
                          cedido{r.owner_company_name ? ` · ${r.owner_company_name}` : ''}
                        </span>
                      )}
                      {picked && <CheckIcon width={15} height={15} className="sw-browse-check" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
        {!busy && !err && !results.length && (
          <div className="sw-search-hint">No hay elementos de este tipo en la empresa.</div>
        )}
      </div>
    </div>
  );
}

// ── Single question ───────────────────────────────────────────────────────────
// ── Selector de CI en MODAL ────────────────────────────────────────────────
// Dentro de una fila de repetidor NO cabe un explorador en línea: al abrirlo se
// montaba encima de los campos de al lado y la fila dejaba de entenderse. En un
// diálogo, la búsqueda, los tipos y la lista tienen todo el ancho y el formulario
// de detrás no se mueve.
function CiPickerModal({
  companyId, title, help, ciTypes, filter, value,
  typeChoice = [], createFields = [], defaultType,
  onPick, onClose,
}) {
  const canCreate = typeChoice.length > 0 || createFields.length > 0;
  const [tab, setTab] = useState(canCreate ? 'pick' : 'pick');
  const [draft, setDraft] = useState({});
  const tipo = draft.ci_type || defaultType || (typeChoice[0] && typeChoice[0].value) || '';
  const setCampo = (fid, v) => setDraft((d) => ({ ...d, fields: { ...(d.fields || {}), [fid]: v } }));
  const puedeCrear = !!tipo && !!String(draft.fields?.name || '').trim();

  const crear = () => {
    if (!puedeCrear) return;
    onPick({ mode: 'create', ci_type: tipo, fields: { ...(draft.fields || {}), name: String(draft.fields.name).trim() } });
    onClose();
  };

  const yaHay = !!(value && (value.ci_id || value.mode === 'create'));

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal sd-model-modal sw-picker" onClick={(e) => e.stopPropagation()}>
        <div className="sd-model-head">
          <h3>🔎 {title || 'Elegir elemento'}</h3>
          <button className="sd-model-x" onClick={onClose} title="Cerrar"><XMarkIcon width={16} height={16} /></button>
        </div>
        {help && <p className="sd-modal-sub">{help}</p>}
        {canCreate && (
          <div className="sw-tabs">
            <button type="button" className={tab === 'pick' ? 'is-on' : ''} onClick={() => setTab('pick')}>Elegir existente</button>
            <button type="button" className={tab === 'create' ? 'is-on' : ''} onClick={() => setTab('create')}>Crear nuevo</button>
          </div>
        )}
        <div className="sw-picker-body">
          {tab === 'pick' ? (
            <CiBrowser companyId={companyId} ciTypes={ciTypes} filter={filter}
              placeholder="Buscar por nombre…"
              onPick={(r) => { onPick(r); onClose(); }} />
          ) : (
            <div className="sw-create-fields">
              {typeChoice.length > 0 && (
                <label className="sw-field">
                  <span>Qué crear</span>
                  <select className="sd-builder-input" value={tipo}
                    onChange={(e) => setDraft({ ...draft, ci_type: e.target.value })}>
                    {typeChoice.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                </label>
              )}
              {createFields.map((f) => (
                <label key={f.id} className="sw-field">
                  <span>{f.label}{f.required ? ' *' : ''}</span>
                  {f.type === 'choice' ? (
                    <select className="sd-builder-input" value={draft.fields?.[f.id] || ''}
                      onChange={(e) => setCampo(f.id, e.target.value)}>
                      <option value="">—</option>
                      {normOptions(f.options).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : (
                    <input className="sd-builder-input" type={f.type === 'number' ? 'number' : 'text'}
                      value={draft.fields?.[f.id] ?? ''}
                      onChange={(e) => setCampo(f.id, e.target.value)} />
                  )}
                </label>
              ))}
              <p className="sw-prev-muted">
                Se creará en la CMDB al confirmar el asistente y se usará en esta fila.
              </p>
            </div>
          )}
        </div>
        <div className="sd-modal-actions sw-actions">
          {yaHay ? (
            <button className="sd-cancel-btn" onClick={() => { onPick(null); onClose(); }}>
              <TrashIcon width={14} height={14} /> Quitar de esta fila
            </button>
          ) : <span />}
          <div className="sw-picker-foot">
            <button className="sd-cancel-btn" onClick={onClose}>Cancelar</button>
            {tab === 'create' && canCreate && (
              <button className="sd-save-btn" disabled={!puedeCrear} onClick={crear}>
                <CheckIcon width={15} height={15} /> Crear y usar
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Selector compacto para las filas de un `repeat`: el botón enseña qué hay
// elegido y abre el selector en un modal (ver CiPickerModal).
function RowPicker({ companyId, field, value, onPick }) {
  const [open, setOpen] = useState(false);
  const typeChoice = normOptions(field.create_type_choice);
  const canCreate = typeChoice.length > 0 || (field.create_fields || []).length > 0;
  const etiqueta = value?.mode === 'create' ? (value.fields?.name || '(sin nombre)') : (value?.name || '');
  const tipoLabel = value?.ci_type ? (CI_TYPE_LABELS[value.ci_type] || value.ci_type) : '';
  return (
    <div className="sw-rowp">
      {open && (
        <CiPickerModal
          companyId={companyId}
          title={field.label}
          help={field.help}
          ciTypes={field.ci_types}
          filter={field.filter}
          value={value}
          typeChoice={typeChoice}
          createFields={field.create_fields || []}
          defaultType={field.ci_type}
          onPick={onPick}
          onClose={() => setOpen(false)}
        />
      )}
      {etiqueta ? (
        <div className="sw-picked sw-picked--row">
          {tipoLabel && <span className="sw-chip">{value.mode === 'create' ? `Nuevo · ${tipoLabel}` : tipoLabel}</span>}
          <span className="sw-rowp-name" title={etiqueta}>{etiqueta}</span>
          <button type="button" className="sw-x" title="Cambiar" onClick={() => setOpen(true)}>
            <PencilSquareIcon width={12} height={12} />
          </button>
          <button type="button" className="sw-x" title="Quitar" onClick={() => onPick(null)}>
            <XMarkIcon width={12} height={12} />
          </button>
        </div>
      ) : (
        <button type="button" className="sw-pick-btn" onClick={() => setOpen(true)}>
          <MagnifyingGlassIcon width={13} height={13} /> Elegir…
          {canCreate && <span className="sw-pick-btn-plus">o crear</span>}
        </button>
      )}
    </div>
  );
}

// Multi-selección que ADEMÁS permite crear elementos al vuelo (p. ej. proteger
// una base de datos que todavía no está en la CMDB). La respuesta es una lista
// con elementos elegidos ({ci_type, ci_id, name}) y/o a crear
// ({mode: 'create', ci_type, fields}).
function MultiPickOrCreate({ q, companyId, value, onChange }) {
  const list = Array.isArray(value) ? value : [];
  const [draft, setDraft] = useState({});
  const [showCreate, setShowCreate] = useState(false);
  const typeChoice = normOptions(q.create_type_choice);
  const isPicked = (r) => list.some((x) => x.ci_type === r.ci_type && x.ci_id === r.ci_id);
  const removeAt = (i) => onChange(list.filter((_, idx) => idx !== i));
  const addPicked = (r) => {
    if (!r) return;
    if (!isPicked(r)) onChange([...list, r]);
  };
  const addCreated = () => {
    const ci_type = draft.ci_type || (typeChoice[0] && typeChoice[0].value) || q.ci_type;
    const name = String(draft.fields?.name || '').trim();
    if (!ci_type || !name) return;
    onChange([...list, { mode: 'create', ci_type, fields: { ...(draft.fields || {}), name } }]);
    setDraft({});
    setShowCreate(false);
  };
  return (
    <div className="sw-poc">
      <div className="sw-poc-head">
        {list.length
          ? <><strong>{list.length}</strong> elemento{list.length === 1 ? '' : 's'} elegido{list.length === 1 ? '' : 's'}</>
          : 'Todavía no has elegido nada'}
      </div>
      {list.length > 0 && (
        <div className="sw-poc-picked">
          {list.map((r, i) => (
            <div className="sw-picked" key={r.ci_id ? `${r.ci_type}:${r.ci_id}` : `new:${i}`}>
              <span className="sw-chip">
                {r.mode === 'create'
                  ? `Nuevo · ${CI_TYPE_LABELS[r.ci_type] || r.ci_type}`
                  : (CI_TYPE_LABELS[r.ci_type] || r.ci_type)}
              </span>
              {r.mode === 'create' ? (r.fields?.name || '(sin nombre)') : r.name}
              <button type="button" className="sw-x" title="Quitar" onClick={() => removeAt(i)}>
                <XMarkIcon width={14} height={14} />
              </button>
            </div>
          ))}
        </div>
      )}
      <CiBrowser companyId={companyId} ciTypes={q.ci_types} filter={q.filter}
        placeholder="Filtrar y añadir…" isPicked={isPicked} onPick={addPicked} />
      <button type="button" className="sd-builder-add" onClick={() => setShowCreate((v) => !v)}>
        <PlusIcon width={13} height={13} /> Crear uno nuevo
      </button>
      {showCreate && (
        <div className="sw-create-fields">
          {typeChoice.length > 0 && (
            <label className="sw-field">
              <span>Qué crear</span>
              <select className="sd-builder-input"
                value={draft.ci_type || (typeChoice[0] && typeChoice[0].value) || ''}
                onChange={(e) => setDraft({ ...draft, ci_type: e.target.value })}>
                {typeChoice.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </label>
          )}
          {(q.create_fields || []).map((f) => (
            <label key={f.id} className="sw-field">
              <span>{f.label}{f.required ? ' *' : ''}</span>
              {f.type === 'choice' ? (
                <select className="sd-builder-input" value={draft.fields?.[f.id] || ''}
                  onChange={(e) => setDraft({ ...draft, fields: { ...(draft.fields || {}), [f.id]: e.target.value } })}>
                  <option value="">—</option>
                  {normOptions(f.options).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              ) : (
                <input className="sd-builder-input" type={f.type === 'number' ? 'number' : 'text'}
                  value={draft.fields?.[f.id] ?? ''}
                  onChange={(e) => setDraft({ ...draft, fields: { ...(draft.fields || {}), [f.id]: e.target.value } })} />
              )}
            </label>
          ))}
          <button type="button" className="sd-builder-add is-on" onClick={addCreated}>
            <PlusIcon width={13} height={13} /> Añadir a la lista
          </button>
        </div>
      )}
    </div>
  );
}

const SCHEDULE_FREQUENCIES = [
  { value: 'daily', label: 'Diaria' },
  { value: 'weekly', label: 'Semanal' },
  { value: 'monthly', label: 'Mensual' },
  { value: 'interval', label: 'Cada N horas' },
  { value: 'custom', label: 'Cron personalizado' },
];

const WEEKDAYS = [
  { value: 1, label: 'L' }, { value: 2, label: 'M' }, { value: 3, label: 'X' },
  { value: 4, label: 'J' }, { value: 5, label: 'V' }, { value: 6, label: 'S' },
  { value: 7, label: 'D' },
];

function WeekdayPicker({ value, onChange }) {
  const days = Array.isArray(value) ? value.map(Number) : [];
  const toggle = (day) => {
    onChange(days.includes(day) ? days.filter((d) => d !== day) : [...days, day].sort((a, b) => a - b));
  };
  return (
    <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
      {WEEKDAYS.map((d) => (
        <button
          key={d.value}
          type="button"
          className={`sd-builder-add${days.includes(d.value) ? ' is-on' : ''}`}
          style={{ minWidth: 30, justifyContent: 'center', padding: '3px 6px' }}
          onClick={() => toggle(d.value)}
          title={d.label}
        >{d.label}</button>
      ))}
    </div>
  );
}

/**
 * Programación completa de una fila de trabajo: los MISMOS campos que
 * `BackupPolicyForm` (frecuencia, hora, días, ventana, cron y retención).
 * Se guarda como objeto y el motor la convierte con `build_cron`.
 */
function ScheduleField({ value, onChange }) {
  const v = (value && typeof value === 'object') ? value : {};
  const set = (key, val) => onChange({ ...v, [key]: val });
  const frequency = v.frequency || '';
  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <select className="sd-builder-input" style={{ maxWidth: 180 }} value={frequency}
          onChange={(e) => set('frequency', e.target.value)}>
          <option value="">(sin programar)</option>
          {SCHEDULE_FREQUENCIES.map((f) => <option key={f.value} value={f.value}>{f.label}</option>)}
        </select>
        {(frequency === 'daily' || frequency === 'weekly' || frequency === 'monthly') && (
          <input className="sd-builder-input" type="time" style={{ maxWidth: 120 }}
            value={v.time || ''} onChange={(e) => set('time', e.target.value)} title="Hora de inicio" />
        )}
        {frequency === 'interval' && (
          <>
            <input className="sd-builder-input" type="number" min="1" max="24" style={{ maxWidth: 90 }}
              value={v.interval_hours || ''} onChange={(e) => set('interval_hours', e.target.value ? Number(e.target.value) : null)}
              placeholder="Cada N h" />
            <input className="sd-builder-input" type="time" style={{ maxWidth: 120 }}
              value={v.window_start || ''} onChange={(e) => set('window_start', e.target.value)} title="Desde" />
            <input className="sd-builder-input" type="time" style={{ maxWidth: 120 }}
              value={v.window_end || ''} onChange={(e) => set('window_end', e.target.value)} title="Hasta" />
          </>
        )}
        {frequency === 'monthly' && (
          <input className="sd-builder-input" type="number" min="1" max="31" style={{ maxWidth: 90 }}
            value={v.day_of_month || ''} onChange={(e) => set('day_of_month', e.target.value ? Number(e.target.value) : null)}
            placeholder="Día del mes" />
        )}
        {frequency === 'custom' && (
          <input className="sd-builder-input" style={{ minWidth: 220 }}
            value={v.custom || ''} onChange={(e) => set('custom', e.target.value)}
            placeholder="0 2 * * *" />
        )}
      </div>
      {(frequency === 'weekly' || frequency === 'interval') && (
        <WeekdayPicker value={v.weekdays} onChange={(days) => set('weekdays', days)} />
      )}
    </div>
  );
}

/**
 * Selector de software instalado en el host elegido. Si el equipo aún no está
 * en la CMDB (se está creando en el asistente) o el producto no aparece, se
 * permite escribirlo a mano; el motor lo registrará como instalación manual.
 */
function InstalledSoftwarePicker({ q, companyId, answers, value, onChange }) {
  const host = answers?.[q.host_from || 'backup_host'];
  const hostRef = host?.ci_id ? host : (host?.ci || null);
  const hostType = hostRef?.ci_type;
  const hostId = hostRef?.ci_id;
  const [rows, setRows] = useState([]);
  const [meta, setMeta] = useState({ categories: [], category_labels: {} });
  const [loading, setLoading] = useState(false);
  const [manual, setManual] = useState(Boolean(value?.manual));

  useEffect(() => {
    if (!companyId) return;
    api.getSoftwareMeta(companyId).then((m) => setMeta(m || {})).catch(() => {});
  }, [companyId]);

  useEffect(() => {
    if (!companyId || !hostType || !hostId) {
      setRows([]);
      return;
    }
    let alive = true;
    setLoading(true);
    api.getSoftwareInstallations(companyId, {
      host_ci_type: hostType,
      host_ci_id: hostId,
      category: q.category || '',
      include_ignored: 0,
    })
      .then((data) => { if (alive) setRows(Array.isArray(data) ? data : []); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [companyId, hostType, hostId, q.category]);

  const selectedId = value?.installation_id;
  const categories = meta.categories || [];
  const categoryLabels = meta.category_labels || {};
  const selectedRow = rows.find((r) => r.id === selectedId);

  return (
    <div className="sw-poc">
      {!hostType || !hostId ? (
        <p className="sw-prev-muted">
          {host?.mode === 'create'
            ? 'El equipo se creará al confirmar el asistente. Escribe el software a mano; quedará enlazado a ese equipo.'
            : 'Elige primero «¿Dónde corre el software de backup?» para buscar el software instalado.'}
        </p>
      ) : loading ? (
        <p className="sw-prev-muted">Buscando software instalado en el equipo…</p>
      ) : rows.length ? (
        <div style={{ display: 'grid', gap: 6 }}>
          {rows.map((r) => (
            <button
              key={r.id}
              type="button"
              className={`sd-builder-add${selectedId === r.id ? ' is-on' : ''}`}
              style={{ justifyContent: 'flex-start', textAlign: 'left', display: 'flex', gap: 8, flexWrap: 'wrap' }}
              onClick={() => {
                setManual(false);
                onChange({
                  installation_id: r.id,
                  name: r.name,
                  version: r.version || '',
                  vendor: r.vendor || '',
                  category: r.category || q.category || 'other',
                  source: r.source,
                  host_ci_type: r.host_ci_type,
                  host_ci_id: r.host_ci_id,
                });
              }}
            >
              <strong>{r.name}</strong>
              <span style={{ opacity: 0.8 }}>{r.version || 'sin versión'}</span>
              <span style={{ opacity: 0.6 }}>{r.vendor || '—'}</span>
              <span className="sw-chip">{r.category_label || r.category}</span>
            </button>
          ))}
        </div>
      ) : (
        <p className="sw-prev-muted">No se ha detectado software de backup en este equipo.</p>
      )}

      {selectedId && !selectedRow && value?.name && (
        <div className="sw-picked" style={{ marginTop: 6 }}>
          <span className="sw-chip">Software</span>{value.name}
          <button type="button" className="sw-x" onClick={() => onChange(null)} title="Quitar">
            <XMarkIcon width={14} height={14} />
          </button>
        </div>
      )}

      <button type="button" className={`sd-builder-add${manual ? ' is-on' : ''}`}
        style={{ marginTop: 6 }}
        onClick={() => {
          const next = !manual;
          setManual(next);
          if (next) onChange({ manual: true, name: value?.name || '', version: value?.version || '', vendor: value?.vendor || '', category: value?.category || q.category || 'backup' });
          else onChange(null);
        }}>
        <PlusIcon width={13} height={13} /> {manual ? 'No añadir manualmente' : 'No aparece / añadir manualmente'}
      </button>

      {manual && (
        <div className="sw-create-fields">
          <label className="sw-field">
            <span>Producto *</span>
            <input className="sd-builder-input" value={value?.name || ''}
              onChange={(e) => onChange({ ...(value || {}), manual: true, name: e.target.value })} />
          </label>
          <label className="sw-field">
            <span>Versión</span>
            <input className="sd-builder-input" value={value?.version || ''}
              onChange={(e) => onChange({ ...(value || {}), manual: true, version: e.target.value })} />
          </label>
          <label className="sw-field">
            <span>Fabricante</span>
            <input className="sd-builder-input" value={value?.vendor || ''}
              onChange={(e) => onChange({ ...(value || {}), manual: true, vendor: e.target.value })} />
          </label>
          <label className="sw-field">
            <span>Categoría</span>
            <select className="sd-builder-input" value={value?.category || q.category || 'backup'}
              onChange={(e) => onChange({ ...(value || {}), manual: true, category: e.target.value })}>
              {categories.map((c) => <option key={c} value={c}>{categoryLabels[c] || c}</option>)}
            </select>
          </label>
        </div>
      )}
    </div>
  );
}

function Question({ q, companyId, answers, value, onChange }) {
  if (q.type === 'installed_software') {
    return <InstalledSoftwarePicker q={q} companyId={companyId} answers={answers} value={value} onChange={onChange} />;
  }
  if (q.type === 'multi_pick_or_create_ci') {
    return <MultiPickOrCreate q={q} companyId={companyId} value={value} onChange={onChange} />;
  }
  if (q.type === 'text') {
    const textVal = typeof value === 'string' ? value : (value?.value ?? '');
    const linked = value && typeof value === 'object' ? value.ci : null;
    const createSpec = value && typeof value === 'object' ? value.create : null;
    const canCreate = !!q.create_ci_type;
    const primary = q.create_primary_field || 'name';
    const cTypeLabel = CI_TYPE_LABELS[q.create_ci_type] || q.create_ci_type;
    // El valor escrito (CIDR, IP…) es también el campo principal del CI nuevo.
    const withCreate = (fields) => onChange({
      value: textVal,
      create: { ci_type: q.create_ci_type, fields: { ...fields, [primary]: textVal } },
    });
    return (
      <div>
        <input className="sd-builder-input" value={textVal} placeholder={q.help || ''}
          onChange={(e) => {
            const v = e.target.value;
            if (createSpec) {
              onChange({ value: v, create: {
                ...createSpec,
                fields: { ...(createSpec.fields || {}), [primary]: v } } });
            } else { onChange(v); }
          }} />
        {linked && (
          <div className="sw-picked">
            <span className="sw-chip">{CI_TYPE_LABELS[linked.ci_type] || linked.ci_type}</span>
            Enlazado: {linked.name}
            <button type="button" className="sw-x" title="Quitar el enlace"
              onClick={() => onChange(textVal)}>
              <XMarkIcon width={14} height={14} />
            </button>
          </div>
        )}
        {canCreate && (
          <div className="sw-create-inline">
            <button type="button" className={`sd-builder-add${createSpec ? ' is-on' : ''}`}
              onClick={() => (createSpec ? onChange(textVal) : withCreate({}))}>
              <PlusIcon width={13} height={13} />
              {createSpec
                ? 'No crear · dejar solo el texto'
                : `Crear ${cTypeLabel}${textVal ? ` «${textVal}»` : ' con este valor'}`}
            </button>
            {createSpec && !!(q.create_fields || []).length && (
              <div className="sw-create-fields">
                {(q.create_fields || []).map((f) => (
                  <label key={f.id} className="sw-field">
                    <span>{f.label}{f.required ? ' *' : ''}</span>
                    {f.type === 'choice' ? (
                      <select className="sd-builder-input" value={createSpec.fields?.[f.id] ?? ''}
                        onChange={(e) => withCreate({ ...(createSpec.fields || {}), [f.id]: e.target.value })}>
                        <option value="">—</option>
                        {normOptions(f.options).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                      </select>
                    ) : (
                      <input className="sd-builder-input" type={f.type === 'number' ? 'number' : 'text'}
                        value={createSpec.fields?.[f.id] ?? ''}
                        onChange={(e) => withCreate({ ...(createSpec.fields || {}),
                          [f.id]: f.type === 'number' ? Number(e.target.value) : e.target.value })} />
                    )}
                  </label>
                ))}
              </div>
            )}
          </div>
        )}
        {!!(q.suggest_ci_types || []).length && (
          <CiBrowser companyId={companyId} ciTypes={q.suggest_ci_types} filter={q.filter}
            placeholder="…o elige uno del mapa para enlazarlo"
            onPick={(r) => onChange({
              value: String(r.name).split(' · ')[0],
              ci: { ci_type: r.ci_type, ci_id: r.ci_id, name: r.name },
            })} />
        )}
      </div>
    );
  }
  if (q.type === 'number') {
    return <input className="sd-builder-input" type="number" value={value ?? ''} placeholder={q.help || ''}
      onChange={(e) => onChange(e.target.value === '' ? null : Number(e.target.value))} />;
  }
  if (q.type === 'yesno') {
    return (
      <div className="sw-yesno">
        <button type="button" className={value === true ? 'is-on' : ''} onClick={() => onChange(true)}>Sí</button>
        <button type="button" className={value === false ? 'is-on' : ''} onClick={() => onChange(false)}>No</button>
      </div>
    );
  }
  if (q.type === 'choice') {
    return (
      <div className="sw-choices">
        {normOptions(q.options).map((o) => (
          <button type="button" key={o.value} className={value === o.value ? 'is-on' : ''}
            onClick={() => onChange(o.value)}>{o.label}</button>
        ))}
      </div>
    );
  }
  if (q.type === 'multichoice') {
    const arr = Array.isArray(value) ? value : [];
    const toggle = (v) => onChange(arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
    return (
      <div className="sw-choices">
        {normOptions(q.options).map((o) => (
          <button type="button" key={o.value} className={arr.includes(o.value) ? 'is-on' : ''}
            onClick={() => toggle(o.value)}>{o.label}</button>
        ))}
      </div>
    );
  }
  if (q.type === 'pick_ci') {
    return (
      <div>
        {value && value.ci_id ? (
          <div className="sw-picked">
            <span className="sw-chip">{CI_TYPE_LABELS[value.ci_type] || value.ci_type}</span>
            {value.name}
            <button type="button" className="sw-x" onClick={() => onChange(null)}><XMarkIcon width={14} height={14} /></button>
          </div>
        ) : (
          <CiBrowser companyId={companyId} ciTypes={q.ci_types} filter={q.filter} onPick={(r) => onChange(r)} />
        )}
      </div>
    );
  }
  if (q.type === 'multi_pick_ci') {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div>
        {arr.map((r) => (
          <div className="sw-picked" key={`${r.ci_type}:${r.ci_id}`}>
            <span className="sw-chip">{CI_TYPE_LABELS[r.ci_type] || r.ci_type}</span>{r.name}
            <button type="button" className="sw-x" onClick={() => onChange(arr.filter((x) => !(x.ci_type === r.ci_type && x.ci_id === r.ci_id)))}>
              <XMarkIcon width={14} height={14} />
            </button>
          </div>
        ))}
        <CiBrowser companyId={companyId} ciTypes={q.ci_types} filter={q.filter} placeholder="Filtrar y añadir…"
          isPicked={(r) => arr.some((x) => x.ci_type === r.ci_type && x.ci_id === r.ci_id)}
          onPick={(r) => { if (!arr.some((x) => x.ci_type === r.ci_type && x.ci_id === r.ci_id)) onChange([...arr, r]); }} />
      </div>
    );
  }
  if (q.type === 'pick_or_create_ci') {
    const mode = value?.mode || 'pick';
    const typeChoice = normOptions(q.create_type_choice);
    const setMode = (m) => {
      const base = { ...(value || {}), mode: m };
      if (m === 'create' && !base.ci_type && typeChoice[0]) base.ci_type = typeChoice[0].value;
      onChange(base);
    };
    return (
      <div className="sw-poc">
        <div className="sw-tabs">
          <button type="button" className={mode === 'pick' ? 'is-on' : ''} onClick={() => setMode('pick')}>Elegir existente</button>
          <button type="button" className={mode === 'create' ? 'is-on' : ''} onClick={() => setMode('create')}>Crear nuevo</button>
        </div>
        {mode === 'pick' ? (
          value?.ci_id ? (
            <div className="sw-picked">
              <span className="sw-chip">{CI_TYPE_LABELS[value.ci_type] || value.ci_type}</span>{value.name}
              <button type="button" className="sw-x" onClick={() => onChange({ mode: 'pick' })}><XMarkIcon width={14} height={14} /></button>
            </div>
          ) : (
            <CiBrowser companyId={companyId} ciTypes={q.ci_types || [q.ci_type]} filter={q.filter} onPick={(r) => onChange({ mode: 'pick', ...r })} />
          )
        ) : (
          <div className="sw-create-fields">
            {typeChoice.length > 0 && (
              <label className="sw-field">
                <span>Qué crear</span>
                <select className="sd-builder-input"
                  value={value?.ci_type || typeChoice[0].value}
                  onChange={(e) => onChange({ ...value, mode: 'create', ci_type: e.target.value })}>
                  {typeChoice.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </label>
            )}
            {(q.create_fields || []).map((f) => (
              <label key={f.id} className="sw-field">
                <span>{f.label}{f.required ? ' *' : ''}</span>
                {f.type === 'choice' ? (
                  <select className="sd-builder-input" value={value?.fields?.[f.id] || ''}
                    onChange={(e) => onChange({ ...value, mode: 'create', fields: { ...(value?.fields || {}), [f.id]: e.target.value } })}>
                    <option value="">—</option>
                    {normOptions(f.options).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                  </select>
                ) : (
                  <input className="sd-builder-input" type={f.type === 'number' ? 'number' : 'text'}
                    value={value?.fields?.[f.id] ?? ''}
                    onChange={(e) => onChange({ ...value, mode: 'create', fields: { ...(value?.fields || {}), [f.id]: f.type === 'number' ? Number(e.target.value) : e.target.value } })} />
                )}
              </label>
            ))}
          </div>
        )}
      </div>
    );
  }
  if (q.type === 'repeat') {
    const rows = Array.isArray(value) ? value : [];
    const fields = q.fields || [];
    const setRow = (i, fid, v) => onChange(rows.map((r, idx) => {
      if (idx !== i) return r;
      const next = { ...r, [fid]: v };
      // El host de réplica solo aplica a trabajos de réplica: si se cambia el
      // tipo, no se arrastra el valor anterior.
      if (fid === 'job' && v !== 'replica') next.replica = null;
      return next;
    }));
    const fieldVisible = (f, row) => {
      const cond = f.show_if;
      if (!cond) return true;
      if (Array.isArray(cond)) return cond.every((c) => fieldVisible({ ...f, show_if: c }, row));
      const key = cond.field || cond.q;
      if (!key) return true;
      const actual = row[key];
      if (cond.empty) return actual === undefined || actual === null || actual === '' || (Array.isArray(actual) && actual.length === 0);
      if (Object.prototype.hasOwnProperty.call(cond, 'eq')) return String(actual ?? '') === String(cond.eq);
      if (Array.isArray(cond.in)) return cond.in.map(String).includes(String(actual ?? ''));
      return true;
    };
    const titulo = (i) => {
      const base = (q.item_label || 'fila').trim();
      return `${base.charAt(0).toUpperCase()}${base.slice(1)} ${i + 1}`;
    };
    return (
      <div className="sw-repeat">
        {rows.map((r, i) => (
          <div className="sw-repeat-row" key={i}>
            {/* Cada fila es una ficha con su cabecera: antes era todo una línea
                de campos y no se veía dónde empezaba ni acababa cada trabajo. */}
            <div className="sw-repeat-head">
              <span className="sw-repeat-title">{titulo(i)}</span>
              <button type="button" className="sw-x" title="Quitar fila"
                onClick={() => onChange(rows.filter((_, idx) => idx !== i))}>
                <TrashIcon width={14} height={14} />
              </button>
            </div>
            <div className="sw-repeat-grid">
              {fields.filter((f) => fieldVisible(f, r)).map((f) => (
                <div key={f.id} className={`sw-field sw-repeat-cell ${(f.type && f.type.startsWith('multi_')) || f.type === 'schedule' ? 'sw-repeat-cell-wide' : ''}`}>
                  <span>{f.label}{f.required ? ' *' : ''}</span>
                  {f.help && <small className="sw-field-help">{f.help}</small>}
                  {f.type === 'choice' ? (
                    <select className="sd-builder-input" value={r[f.id] ?? ''}
                      onChange={(e) => setRow(i, f.id, e.target.value)}>
                      <option value="">—</option>
                      {normOptions(f.options).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  ) : f.type === 'schedule' ? (
                    <ScheduleField value={r[f.id]} onChange={(v) => setRow(i, f.id, v)} />
                  ) : (f.type === 'multi_pick_ci' || f.type === 'multi_pick_or_create_ci') ? (
                    <Question q={f} companyId={companyId} answers={r} value={r[f.id]}
                      onChange={(v) => setRow(i, f.id, v)} />
                  ) : (f.type === 'pick_ci' || f.type === 'pick_or_create_ci') ? (
                    <RowPicker companyId={companyId} field={f} value={r[f.id]}
                      onPick={(ci) => setRow(i, f.id, ci)} />
                  ) : (
                    <input className="sd-builder-input" type={f.type === 'number' ? 'number' : 'text'}
                      value={r[f.id] ?? ''}
                      onChange={(e) => setRow(i, f.id, f.type === 'number'
                        ? (e.target.value === '' ? null : Number(e.target.value))
                        : e.target.value)} />
                  )}
                </div>
              ))}
            </div>
          </div>
        ))}
        <button type="button" className="sd-builder-add sw-repeat-add" onClick={() => onChange([...rows, {}])}>
          <PlusIcon width={13} height={13} /> Añadir {q.item_label || 'fila'}
        </button>
      </div>
    );
  }
  return null;
}

export default function ServiceWizardRunner({ companyId, onClose, onCreated }) {
  const [wizards, setWizards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [wizard, setWizard] = useState(null);
  const [idx, setIdx] = useState(0);          // -1 = catalog; 0 = service fields; 1..N = steps; N+1 = preview
  const [service, setService] = useState({ name: '', criticality: 'medium', status: 'running', description: '' });
  const [answers, setAnswers] = useState({});
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.swList(companyId)
      .then((r) => setWizards(r.data || []))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, [companyId]);

  const steps = wizard?.definition?.steps || [];
  // Un paso cuyas preguntas no aplican (show_if) desaparece del recorrido.
  const visibleSteps = steps.filter((s) =>
    (s.questions || []).some((q) => showQuestion(q, answers)));
  // OJO: el recorrido va sobre los pasos VISIBLES. Si la previsualización se
  // indexa con `steps.length` (el total de pasos de la definición), al ocultarse
  // algún paso `isPreview` nunca es cierto y el asistente se queda colgado en el
  // último paso con el panel en blanco. Es un único índice para los tres sitios.
  const previewIdx = visibleSteps.length + 1;
  const totalPanels = previewIdx + 1; // service fields + steps + preview

  const startWizard = (w) => {
    setWizard(w); setIdx(0); setAnswers({}); setPreview(null);
    setService((s) => ({ ...s, name: '' }));
  };

  const setAnswer = (qid, v) => setAnswers((a) => {
    const next = { ...a, [qid]: v };
    // Preguntas dependientes declaradas en la definición (p. ej. el host cambia
    // → el software detectado ya no es el mismo).
    const q = (wizard?.definition?.steps || [])
      .flatMap((s) => s.questions || [])
      .find((x) => x.id === qid);
    (q?.clear_on_change || []).forEach((k) => { delete next[k]; });
    return next;
  });

  const goPreview = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.swPreview(companyId, wizard.id, { service, answers });
      setPreview(r.data); setIdx(previewIdx);
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const apply = async () => {
    setBusy(true); setError(null);
    try {
      const r = await api.swApply(companyId, wizard.id, { service, answers });
      const sid = r.data?.service?.id;
      if (onCreated && sid) onCreated(sid, service.name);
      onClose();
    } catch (e) { setError(e.message); } finally { setBusy(false); }
  };

  const next = () => {
    if (idx === visibleSteps.length) { goPreview(); return; }
    setIdx((i) => i + 1);
  };

  const back = () => setIdx((i) => Math.max(0, i - 1));

  // ── Catalog ──
  if (!wizard) {
    return (
      <div className="sd-modal-backdrop" onClick={onClose}>
        <div className="sd-modal sd-model-modal sw-modal" onClick={(e) => e.stopPropagation()}>
          <div className="sd-model-head">
            <h3>🧭 Asistentes de servicio</h3>
            <button className="sd-model-x" onClick={onClose}><XMarkIcon width={16} height={16} /></button>
          </div>
          <p className="sd-modal-sub">Elige un asistente. Te guiará con preguntas y podrás crear elementos al vuelo.</p>
          {error && <div className="sd-error-banner">{error}</div>}
          {loading ? <div className="sw-search-hint">Cargando…</div> : (
            <div className="sw-cards">
              {wizards.map((w) => (
                <button key={w.id} className="sw-card" onClick={() => startWizard(w)}>
                  <span className="sw-card-icon">{w.icon || '🧩'}</span>
                  <span className="sw-card-body">
                    <strong>{w.label}</strong>
                    <small>{w.description}</small>
                  </span>
                </button>
              ))}
              {!wizards.length && <div className="sw-search-hint">No hay asistentes activos.</div>}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Running ──
  const isServiceStep = idx === 0;
  const isPreview = idx === previewIdx;
  const step = (!isServiceStep && !isPreview) ? visibleSteps[idx - 1] : null;

  return (
    <div className="sd-modal-backdrop" onClick={onClose}>
      <div className="sd-modal sd-model-modal sw-modal" onClick={(e) => e.stopPropagation()}>
        <div className="sd-model-head">
          <h3>{wizard.icon} {wizard.label}</h3>
          <button className="sd-model-x" onClick={onClose}><XMarkIcon width={16} height={16} /></button>
        </div>

        <div className="sw-progress">
          {Array.from({ length: totalPanels }).map((_, i) => (
            <span key={i} className={`sw-dot ${i === idx ? 'is-cur' : ''} ${i < idx ? 'is-done' : ''}`} />
          ))}
        </div>

        {error && <div className="sd-error-banner">{error}<button onClick={() => setError(null)}><XMarkIcon width={14} height={14} /></button></div>}

        <div className="sw-panel">
          {isServiceStep && (
            <>
              <h4 className="sw-step-title">Datos del servicio</h4>
              <label className="sw-field"><span>Nombre *</span>
                <input className="sd-builder-input" value={service.name} autoFocus
                  onChange={(e) => setService({ ...service, name: e.target.value })} placeholder="p. ej. Backup Diario Oficina" />
              </label>
              <label className="sw-field"><span>Criticidad</span>
                <select className="sd-builder-input" value={service.criticality}
                  onChange={(e) => setService({ ...service, criticality: e.target.value })}>
                  {CRITICALITY.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </label>
              <label className="sw-field"><span>Descripción</span>
                <textarea className="sd-builder-input sd-builder-area" rows={2} value={service.description}
                  onChange={(e) => setService({ ...service, description: e.target.value })} />
              </label>
            </>
          )}

          {step && (
            <>
              <h4 className="sw-step-title">{step.title}</h4>
              {step.help && <p className="sw-step-help">{step.help}</p>}
              {(step.questions || []).filter((q) => showQuestion(q, answers)).map((q) => (
                <div className="sw-question" key={q.id}>
                  <label className="sw-q-label">{q.label}{q.required ? ' *' : ''}</label>
                  {q.help && <p className="sw-q-help">{q.help}</p>}
                  <Question q={q} companyId={companyId} answers={answers} value={answers[q.id]} onChange={(v) => setAnswer(q.id, v)} />
                </div>
              ))}
            </>
          )}

          {isPreview && preview && (
            <div className="sw-preview">
              <h4 className="sw-step-title">Revisa lo que se va a crear</h4>
              {!!(preview.warnings || []).length && (
                <div className="sw-warnings">
                  {preview.warnings.map((w, i) => (
                    <div key={i} className="sw-warning">⚠️ {w}</div>
                  ))}
                </div>
              )}
              <div className="sw-prev-block">
                <strong>Servicio:</strong> {preview.service.name}
                <span className="sw-chip">{preview.service.service_type}</span>
              </div>
              {preview.software && (
                <div className="sw-prev-block">
                  <strong>Software:</strong> {preview.software.name}
                  {preview.software.version ? <span className="sw-prev-muted"> · v{preview.software.version}</span> : null}
                  {preview.software.vendor ? <span className="sw-prev-muted"> · {preview.software.vendor}</span> : null}
                  {preview.software.host_name ? <span className="sw-prev-muted"> · en {preview.software.host_name}</span> : null}
                  <span className="sw-chip">{preview.software.source === 'agent' ? 'detectado' : 'manual'}</span>
                </div>
              )}
              {!!preview.members.length && (
                <div className="sw-prev-block">
                  <strong>Elementos ({preview.members.length})</strong>
                  <ul>
                    {preview.members.map((m, i) => (
                      <li key={i}>
                        <span className="sw-chip">{CI_TYPE_LABELS[m.ci_type] || m.ci_type}</span>
                        {m.name}
                        {m.created && !m.ci_id && <span className="sw-badge-new">＋ nuevo</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!!preview.relations.length && (
                <div className="sw-prev-block">
                  <strong>Relaciones ({preview.relations.length})</strong>
                  <ul>
                    {preview.relations.map((r, i) => (
                      <li key={i}>{r.from.name || 'Servicio'} <em>{r.display}</em> {r.to.name || 'Servicio'}</li>
                    ))}
                  </ul>
                </div>
              )}
              {!!(preview.backup_policies || []).length && (
                <div className="sw-prev-block">
                  <strong>Trabajos de backup ({preview.backup_policies.length})</strong>
                  <ul>
                    {preview.backup_policies.map((p, i) => (
                      <li key={i}>
                        <span className="sw-chip">{p.job_label}</span>
                        {p.name}
                        <span className="sw-prev-muted">
                          {' · '}{(p.targets || []).filter(Boolean).join(', ') || 'sin origen indicado'}
                          {p.repository ? ` → ${p.repository}` : ''}
                          {p.replica_host ? ` · expuesta en ${p.replica_host}` : ''}
                          {' · '}
                          {p.schedule_summary
                            || (p.copy_trigger === 'after_backup' ? 'al terminar el backup'
                              : (p.schedule || 'sin programación'))}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!!(preview.db_instances || []).length && (
                <div className="sw-prev-block">
                  <strong>Instancias de base de datos ({preview.db_instances.length})</strong>
                  <ul>
                    {preview.db_instances.map((d, i) => (
                      <li key={i}>
                        <span className="sw-chip">{d.dbms}{d.version ? ` ${d.version}` : ''}</span>
                        {d.name}
                        {d.port ? <span className="sw-prev-muted"> · puerto {d.port}</span> : null}
                        {d.role_label ? <span className="sw-prev-muted"> · {d.role_label}</span> : null}
                        {d.copy_of ? <span className="sw-prev-muted"> · copia de {d.copy_of}</span> : null}
                        {d.host ? <span className="sw-prev-muted"> · en {d.host}</span> : null}
                        {!!(d.databases || []).length && (
                          <div className="sw-prev-muted" style={{ marginLeft: '0.25rem' }}>
                            🗃️ {d.databases.join(' · ')}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!!(preview.groups || []).length && (
                <div className="sw-prev-block">
                  <strong>Grupos en el diseño ({preview.groups.length})</strong>                  <ul>
                    {preview.groups.map((g, i) => (
                      <li key={i}>
                        <span className="sw-chip sw-chip-group">{g.icon || '🗂️'} {g.label}</span>
                        <span className="sw-prev-muted">
                          {g.detail ? `${g.detail} · ` : ''}{g.members.join(' · ')}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!!(preview.service_groups || []).length && (
                <div className="sw-prev-block">
                  <strong>Plataforma / suite ({preview.service_groups.length})</strong>
                  <ul>
                    {preview.service_groups.map((g, i) => (
                      <li key={i}>
                        <span className="sw-chip sw-chip-group">{g.icon || '🧩'} {g.name}</span>
                        <span className="sw-prev-muted">
                          {g.role ? `${g.role} · ` : ''}
                          {g.exists
                            ? 'se añadirá al grupo que ya existe'
                            : 'se creará el grupo con este servicio'}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {!!(preview.dhcp_scopes || []).length && (
                <div className="sw-prev-block">
                  <strong>DHCP de las subredes ({preview.dhcp_scopes.length})</strong>
                  <ul>
                    {preview.dhcp_scopes.map((d, i) => (
                      <li key={i}>
                        {d.error ? (
                          <span className="sw-prev-warn">⚠️ {d.subnet}: {d.error}</span>
                        ) : (
                          <>
                            <span className="sw-chip">{d.provider || 'sin servidor'}</span>
                            sirve DHCP a <strong>{d.subnet}</strong>
                            <span className="sw-prev-muted">
                              {' · '}{d.pool}
                              {d.domain ? ` · dominio ${d.domain}` : ''}
                              {(d.dns || []).length ? ` · DNS ${d.dns.join(', ')}` : ''}
                            </span>
                          </>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
              {(preview.host_ci || preview.service?.hosting_type) && (
                <div className="sw-prev-block">
                  <strong>Alojado en:</strong>{' '}
                  {preview.host_ci ? preview.host_ci.name : 'sin host (servicio externo)'}
                  {preview.service?.hosting_type && (
                    <span className="sw-prev-muted">
                      {' · '}{HOSTING_LABELS[preview.service.hosting_type] || preview.service.hosting_type}
                    </span>
                  )}
                </div>
              )}
              {/* Cierra la duda de "y esto donde se ve luego": todo lo respondido se
                  guarda en el servicio y se consulta en su ficha. */}
              <p className="sw-prev-muted" style={{ marginTop: 10 }}>
                Al crear el servicio, todo lo que has respondido se guarda en él: lo verás en
                su ficha, pestaña <strong>🧾 Datos del asistente</strong>, y el detalle del
                elemento del Diseñador.
              </p>
            </div>
          )}
        </div>

        <div className="sd-modal-actions sw-actions">
          <button className="sd-cancel-btn" onClick={idx === 0 ? () => setWizard(null) : back}>
            <ArrowLeftIcon width={15} height={15} /> {idx === 0 ? 'Asistentes' : 'Atrás'}
          </button>
          {!isPreview ? (
            <button className="sd-save-btn" disabled={busy || (isServiceStep && !service.name.trim())} onClick={next}>
              {idx === visibleSteps.length ? 'Ver resumen' : 'Siguiente'} <ArrowRightIcon width={15} height={15} />
            </button>
          ) : (
            <button className="sd-save-btn" disabled={busy} onClick={apply}>
              <CheckIcon width={16} height={16} /> {busy ? 'Creando…' : 'Crear servicio'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
